import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Tooltip } from "@mui/material";
import { extrairPartesEndereco } from "core/utils/extrairEnderecoComDoisPontos.ts";
import { isNil } from "lodash";
import { ReactNode } from "react";
import { isDataDoisMil } from "../../../../../core/utils/UtilityDates.ts";
import { Embarque } from "../../../../../models/Embarque.ts";

export const defaultRowHeight = 15;
export const DOUBLE_CLICK_DELAY = 250;

const integersFields = [
    "cte",
    "numeroEixos",
    "pedido"
];

export type ExtraEntregaRowProps = {
    dados: any[];
    gridTemplateColumns: string;
    columnWidths: number[];
};

export type ExtraRowProps = {
    dados: Embarque[];
    visibleColumns: any[];
    gridTemplateColumns: string;
};

export function renderCellContent(
    coluna: any,
    row: any,
    options: {
        configEntrega?: boolean;
        configDataHora?: boolean;
        linhaSelecionada: number[];
        showDropDowEmbarques: (row: any) => void;
        classeSelecionado?: string;
        setColunaSelecionada?: (value: any) => void;
        setOpenRegras?: (value: boolean) => void;
    }
): ReactNode {
    const field = coluna?.field ?? coluna?.campo ?? coluna?.nome;
    const { configDataHora, linhaSelecionada, showDropDowEmbarques, setColunaSelecionada, setOpenRegras, classeSelecionado } = options;

    if (field === "unidEmbarque") {
        const temEntrega =
            (row.entrega?.length ?? 0) > 0 && (options.configEntrega ?? true);
        return (
            <>
                {
                    temEntrega ? (
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            width: "100%",
                            justifyContent: "space-between",
                        }} >
                            {
                                linhaSelecionada.includes(row.idEmbarque) ? (
                                    <KeyboardArrowUpIcon
                                        className="icon-entrega"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            showDropDowEmbarques(row);
                                        }
                                        }
                                    />
                                ) : (
                                    <KeyboardArrowDownIcon
                                        className="icon-entrega"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            showDropDowEmbarques(row);
                                        }
                                        }
                                    />
                                )}
                            <span className="no-margin" style={{
                                display: "flex", alignItems: "center",
                            }}> {row.unidEmbarque} </span>
                            < div style={{ width: 12 }} />
                        </div>
                    ) : (
                        <span style={{ margin: 6 }} > {row.unidEmbarque} </span>
                    )}
            </>
        );
    }

    if (field === "rota") {
        const { freteMinimoMotorista, quantidadeExcedidaPiso, abaixoPisoAntt } = row;
        if (!freteMinimoMotorista) {
            return row.rota ?? "";
        }

        const tituloTooltip = (
            <p>
                {abaixoPisoAntt === true ? (
                    <>
                        {`Abaixo do valor mínimo de frete: ${freteMinimoMotorista.toLocaleString(
                            "pt-BR",
                            { style: "currency", currency: "BRL" }
                        )}`}
                        <br />
                    </>
                ) : (
                    <>
                        {
                            freteMinimoMotorista.toLocaleString("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                            })
                        }
                        < br />
                    </>
                )}
                {
                    `Quantidade excedida de piso: ${quantidadeExcedidaPiso
                        ? quantidadeExcedidaPiso.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                        })
                        : "0,00"
                    }`
                }
            </p>
        );

        return (
            <Tooltip title={tituloTooltip} >
                <span className="no-margin"
                    style={{
                        cursor: "pointer",
                        display: "block",
                        width: "100%",
                        height: "100%",
                    }}
                > {row.rota} </span>
            </Tooltip>
        );
    }

    if (["cliente", "observacao", "destinatario", "embarcador", "motivo", "proprietario"].includes(field)) {
        return (
            <Tooltip title={row[field] || ""} >
                <span className="no-margin"
                    style={{
                        cursor: "pointer",
                        display: "block",
                        width: "100%",
                        height: "100%",
                    }
                    }
                > {row[field]} </span>
            </Tooltip>
        );
    }

    if (
        field === "notificarAutomatico" ||
        field === "cancelado" ||
        field === "comprovante" ||
        field === "checkInFeito"
    ) {
        return row[field] ? "Sim" : "Não";
    }

    if (field === "nEntregas" && row.nEntregas) {
        return `${row.nEntregasFeitas}/${row.nEntregas}`;
    }

    if (field === "entrega") {
        return row.entrega?.length || 0;
    }

    if (field === "aceite") {
        return (
            <span className="no-margin"
                onClick={(e) => {
                    e.stopPropagation();
                    if (setColunaSelecionada && setOpenRegras && classeSelecionado) {
                        setColunaSelecionada(row[classeSelecionado]);
                        setOpenRegras(true);
                    }
                }
                }
            >
                {row.aceite}
            </span>
        );
    }

    if (field === "usuarioResponsavel") {
        return row.usuarioResponsavel?.usuario || "";
    }

    const value = (row as any)[field];
    return formatCellValue({ value, configDataHora, field });
}

export function renderEntregaCellContent(coluna: any, row: any): React.ReactNode {
    if (coluna?.field === "freteFiscal") {
        if (!row.freteFiscal) {
            return row.pedido.freteFiscal;
        }
    }

    const field = coluna?.field ?? coluna?.campo ?? coluna?.nome;
    const value = (row as any)[field];
    return formatCellValue({ value, field });
}

export function formatCellValue<T>({ value, configDataHora, field }: { value: unknown, configDataHora?: boolean, field?: string | keyof T | symbol }): React.ReactNode {
    if (value == null) return "";

    if (isDataDoisMil(value as string)) {
        return "";
    }

    if (typeof value === "number") {
        if (integersFields.includes(field as string)) {
            return value.toString();
        }
        if (value === 0) {
            return "0,00";
        }
        return value.toLocaleString("pt-BR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
    }

    const isIsoString =
        typeof value === "string" &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);

    const asDate =
        value instanceof Date
            ? value
            : isIsoString
                ? new Date(value)
                : null;

    if (asDate && !Number.isNaN(asDate.getTime())) {
        if (["nascimento"].includes(field as string)) {
            return asDate.toLocaleDateString("pt-BR", {
                month: "numeric",
                day: "numeric",
                year: "numeric",
            });
        }
        if (["ultimoLogin", "ultimoLogout"].includes(field as string)) {
            return asDate.toLocaleDateString("pt-BR", {
                month: "numeric",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "numeric",
            });
        }
        return asDate.toLocaleDateString("pt-BR", {
            month: "numeric",
            day: "numeric",
            ...(configDataHora
                ? { hour: "numeric", minute: "numeric" }
                : {}),
        });
    }

    if (typeof value === "boolean") {
        return value ? "Sim" : "Não";
    }

    if (typeof value === "object") {
        if ("nome" in value) {
            return value.nome as string;
        }
        if ("descricao" in value) {
            return value.descricao as string;
        }
        if ("idEndereco" in value) {
            const endereco = (value as any);
            return `${endereco.rua ?? ""}, ${endereco.numero ?? ""}, ${endereco.bairro ?? ""
                } - ${endereco.cidade ?? ""}`;
        }
        return JSON.stringify(value);
    }
    
    if ("enderecoMotorista" === field) {
        return extrairPartesEndereco(value as any);
    }

    return String(value);
}

export const getCellClasses = (
    coluna: any,
    row: any,
    algumaDataVencida: boolean
) => {
    const field = coluna?.field ?? coluna?.campo ?? coluna?.nome;
    const classes: string[] = ["tabela-virtualizada-cell"];

    if (coluna.tipo === "number") {
        classes.push("td-right");
    }


    if (field === "motorista") {
        if (row.coordenadasRastreioMotorista !== null) {
            classes.push("rastreio");
        }
    }

    if (["km"].includes(field)) {
        classes.push("td-right");
    }

    if (["margem"].includes(field)) {
        classes.push("td-right-no-padding");
    }

    if (field === "placaCavalo") {
        if (row.caminhao !== null) {
            classes.push("rastreio");
        } else if (row.temApiSul === true) {
            classes.push("td-left-veiculo");
        }
    }

    if (field === "placaCarreta" && row.carreta !== null) {
        classes.push("rastreio");
    }

    if (field === "placaCarreta2" && row.carreta2 !== null) {
        classes.push("rastreio");
    }

    if (field === "unidEmbarque") {
        if (row.entrega && row.entrega.length) {
            classes.push("v-entrega");
        } else {
            classes.push("td-center");
        }
    }

    if (
        field === "embarcadorEstado" ||
        field === "estadoOrigem" ||
        field === "estadoDestino" ||
        field === "destinatarioEstado" ||
        field === "dataCheckagem" ||
        field === "proximaRevisaoData" ||
        field === "aceite"
    ) {
        classes.push("td-center");
    }

    if (field === "freteFiscal" || field === "freteMotorista") {
        if (row.valorVariavelAlto) {
            classes.push("td-center-frete-fiscal");
        } else {
            classes.push("td-right");
        }
    }

    if (field === "rota" && row.abaixoPisoAntt === true && row.freteMinimoMotorista) {
        classes.push("data-piscando-1");
    }

    if (field === "previsaoEmbarque" && row.tempoFaltanteEmbarque != null) {
        if (row.tempoFaltanteEmbarque === 1) classes.push("data-piscando-1");
        else if (row.tempoFaltanteEmbarque === 2) classes.push("data-piscando-2");
        else if (row.tempoFaltanteEmbarque === 3) classes.push("data-piscando-3");
        else if (row.tempoFaltanteEmbarque === 4) classes.push("data-piscando-4");
    }

    if (field === "previsaoEntrega" && row.tempoFaltanteEntrega != null) {
        if (row.tempoFaltanteEntrega === 1) classes.push("data-piscando-1");
        else if (row.tempoFaltanteEntrega === 2) classes.push("data-piscando-2");
        else if (row.tempoFaltanteEntrega === 3) classes.push("data-piscando-3");
        else if (row.tempoFaltanteEntrega === 4) classes.push("data-piscando-4");
    }

    if (field === "dataHoraChegadaEmbarque" && row.onTimeEmbarque) {
        classes.push("data-piscando-1");
    }

    if (field === "dataHoraChegadaEntrega") {
        if (row.onTimeEntrega) {
            classes.push("data-piscando-1");
        } else if (row.excedeuDiariaCliente && !isDataDoisMil(row.dataHoraChegadaEntrega)) {
            classes.push("data-piscando-5");
        }
    }

    if (algumaDataVencida) {
        classes.push("data-piscando-1");
    }

    return classes.filter(Boolean).join(" ");
};

type EmbarqueRow = any;

export function formatValueForMeasure(value: unknown, configDataHora?: boolean): string {
    if (isNil(value)) return "";

    if (isDataDoisMil(value as string)) return "";

    if (typeof value === "number") {
        return Number.isInteger(value) ? value.toString() : value.toFixed(2);
    }

    const isIsoString =
        typeof value === "string" &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);

    const asDate =
        value instanceof Date
            ? value
            : isIsoString
                ? new Date(value)
                : null;

    if (asDate && !Number.isNaN(asDate.getTime())) {
        return asDate.toLocaleDateString("pt-BR", {
            month: "numeric",
            day: "numeric",
            ...(configDataHora ? { hour: "numeric", minute: "numeric" } : {}),
        });
    }

    if (typeof value === "boolean") {
        return value ? "Sim" : "Não";
    }

    if (typeof value === "object") {
        if ("nome" in (value as any)) return String((value as any).nome);
        if ("descricao" in (value as any)) return String((value as any).descricao);
        return JSON.stringify(value);
    }

    return String(value);
}

const regexRemoveSpecialChars = (text: string): string => {
    return text.replace(/[.\-/]/g, "");
};

const FIELD_WIDTHS: Record<string, { min: number; max: number }> = {
    observacao: { min: 40, max: 800 },
    aceite: { min: 40, max: 85 },
    rota: { min: 40, max: 169 },
    usuarioResponsavel: { min: 40, max: 80 },
    placaCarreta: { min: 80, max: 200 },
    placaCavalo: { min: 80, max: 200 },
    placaCarreta2: { min: 80, max: 200 },
    unidEmbarque: { min: 60, max: 200 },
    adiantamento: { min: 55, max: 200 },
    saldo: { min: 55, max: 200 },
    pedagio: { min: 55, max: 200 },
    classificacao: { min: 45, max: 200 },
    icms: { min: 80, max: 200 },
    pedidoId: { min: 80, max: 200 },
    margem: { min: 60, max: 200 },
    freteMotorista: { min: 80, max: 200 },
    freteFiscal: { min: 70, max: 200 },
    adicionalEmpresa: { min: 55, max: 120 },
    adicionalMotorista: { min: 55, max: 120 },
    peso: { min: 60, max: 120 },
    km: { min: 70, max: 120 },
};

export function calcularLargurasColunas(
    rows: EmbarqueRow[],
    visibleColumns: any[],
    configDataHora?: boolean,
    maxRowsToScan = 300
): number[] {
    if (!rows?.length || !visibleColumns?.length) return [];

    const headerPxPorCaractere = 7.2;
    const bodyPxPorCaractere = 6.8;
    const rowsToScan = rows.slice(0, Math.min(rows.length, maxRowsToScan));

    return visibleColumns.map((coluna) => {
        const field = coluna?.field ?? coluna?.campo ?? coluna?.nome;
        const { min = 40, max = 200 } = FIELD_WIDTHS[field] || {};

        const headerText = regexRemoveSpecialChars(
            String(coluna?.nomeColuna || coluna?.label || field || "")
        );
        const headerPixels = headerText.length * headerPxPorCaractere;

        const maxBodyPixels = rowsToScan.reduce((maxPixels, row) => {
            const rawValue = row[field];
            const text = formatValueForMeasure(rawValue, configDataHora);
            return Math.max(maxPixels, text.length * bodyPxPorCaractere);
        }, 0);

        const baseWidth = Math.max(headerPixels, maxBodyPixels);
        return Math.max(min, Math.min(baseWidth, max));
    });
}